import type { TContentBlockBase } from '@onesource/schemas';
import { ContentBlockResourceIcon, ContentBlockResourceType, ContentBlockType } from '@onesource/schemas';
import type { IconType } from 'react-icons';
import { LuClipboardList, LuFileText, LuNotebookText } from 'react-icons/lu';
import {
  MdChecklist,
  MdCloudUpload,
  MdCode,
  MdLink,
  MdMenuBook,
  MdOutlineAssignment,
  MdOutlineHeadphones,
  MdOutlineImage,
  MdOutlineQuiz,
  MdOutlineSlideshow,
  MdOutlineTableChart,
  MdPictureAsPdf,
  MdPlayCircle,
} from 'react-icons/md';
import { RiPaletteLine } from 'react-icons/ri';
import { SiGoogledocs } from 'react-icons/si';

export interface ContentBlockIconMeta {
  icon: IconType;
  label: string;
}

/**
 * Icons an admin can pick for a content block. Order here is the order shown
 * in the dashboard picker.
 */
export const CONTENT_BLOCK_ICONS: Record<ContentBlockResourceIcon, ContentBlockIconMeta> = {
  [ContentBlockResourceIcon.PDF]: { icon: MdPictureAsPdf, label: 'PDF' },
  [ContentBlockResourceIcon.DOCUMENT]: { icon: LuFileText, label: 'Document' },
  [ContentBlockResourceIcon.FORM]: { icon: MdOutlineAssignment, label: 'Form' },
  [ContentBlockResourceIcon.WORKSHEET]: { icon: LuClipboardList, label: 'Worksheet' },
  [ContentBlockResourceIcon.WORKBOOK]: { icon: LuNotebookText, label: 'Workbook' },
  [ContentBlockResourceIcon.CHECKLIST]: { icon: MdChecklist, label: 'Checklist' },
  [ContentBlockResourceIcon.VIDEO]: { icon: MdPlayCircle, label: 'Video' },
  [ContentBlockResourceIcon.QUIZ]: { icon: MdOutlineQuiz, label: 'Quiz' },
  [ContentBlockResourceIcon.LINK]: { icon: MdLink, label: 'Link' },
  [ContentBlockResourceIcon.CANVA]: { icon: RiPaletteLine, label: 'Canva' },
  [ContentBlockResourceIcon.GOOGLE_DOC]: { icon: SiGoogledocs, label: 'Google Doc' },
  [ContentBlockResourceIcon.EMBED]: { icon: MdCode, label: 'Embed' },
  [ContentBlockResourceIcon.UPLOAD]: { icon: MdCloudUpload, label: 'Upload' },
  [ContentBlockResourceIcon.BOOK]: { icon: MdMenuBook, label: 'Book' },
  [ContentBlockResourceIcon.SPREADSHEET]: { icon: MdOutlineTableChart, label: 'Spreadsheet' },
  [ContentBlockResourceIcon.PRESENTATION]: { icon: MdOutlineSlideshow, label: 'Presentation' },
  [ContentBlockResourceIcon.IMAGE]: { icon: MdOutlineImage, label: 'Image' },
  [ContentBlockResourceIcon.AUDIO]: { icon: MdOutlineHeadphones, label: 'Audio' },
};

/** Ordered icon options for selectors, derived from the source map. */
export const CONTENT_BLOCK_ICON_OPTIONS = (Object.keys(CONTENT_BLOCK_ICONS) as ContentBlockResourceIcon[]).map(
  value => ({ value, ...CONTENT_BLOCK_ICONS[value] }),
);

/** Preset icon + label for each resource type an admin can tag a block with. */
export const RESOURCE_TYPE_META: Record<ContentBlockResourceType, ContentBlockIconMeta> = {
  [ContentBlockResourceType.VIDEO]: { icon: MdPlayCircle, label: 'Video' },
  [ContentBlockResourceType.PDF]: { icon: MdPictureAsPdf, label: 'Download' },
  [ContentBlockResourceType.TEXT]: { icon: LuFileText, label: 'Document' },
  [ContentBlockResourceType.DOCUMENT]: { icon: LuFileText, label: 'Document' },
  [ContentBlockResourceType.CANVA_TEMPLATE]: { icon: RiPaletteLine, label: 'Canva Template' },
  [ContentBlockResourceType.GOOGLE_DOC]: { icon: SiGoogledocs, label: 'Google Doc' },
  [ContentBlockResourceType.WORKSHEET]: { icon: LuClipboardList, label: 'Worksheet' },
  [ContentBlockResourceType.WORKBOOK]: { icon: LuNotebookText, label: 'Workbook' },
  [ContentBlockResourceType.QUIZ]: { icon: MdOutlineQuiz, label: 'Quiz' },
  [ContentBlockResourceType.REDIRECT]: { icon: MdLink, label: 'Link' },
  [ContentBlockResourceType.FORM]: { icon: MdOutlineAssignment, label: 'Form' },
  [ContentBlockResourceType.EMBED]: { icon: MdOutlineAssignment, label: 'Form' },
};

/**
 * What a block shows when the admin left "Resource type" empty — its block
 * type's own icon and wording. Only the types that reach the document list
 * need an entry; anything else falls through to Document.
 */
const BLOCK_TYPE_RESOURCE_META: Partial<Record<ContentBlockType, ContentBlockIconMeta>> = {
  [ContentBlockType.PDF]: RESOURCE_TYPE_META[ContentBlockResourceType.PDF],
  [ContentBlockType.REDIRECT]: RESOURCE_TYPE_META[ContentBlockResourceType.REDIRECT],
  [ContentBlockType.FORM]: RESOURCE_TYPE_META[ContentBlockResourceType.FORM],
  [ContentBlockType.EMBED]: { icon: MdCode, label: 'Embed' },
  [ContentBlockType.GCS_UPLOAD]: { icon: MdCloudUpload, label: 'Upload' },
};

/** The preset a block gets from its resource type, else its block type. */
const presetResourceMeta = (block: Pick<TContentBlockBase, 'blockType' | 'resourceType'>): ContentBlockIconMeta =>
  (block.resourceType ? RESOURCE_TYPE_META[block.resourceType] : undefined) ??
  BLOCK_TYPE_RESOURCE_META[block.blockType] ??
  RESOURCE_TYPE_META[ContentBlockResourceType.TEXT];

/**
 * Icon + label for one block. The label always comes from the preset (resource
 * type, else block type); the icon is the admin's override when one is set, so
 * blocks nobody has edited keep exactly their preset. Every surface — member
 * cards, the "In this lesson" summary, the dashboard — reads this one helper so
 * a block can never show different icons in different places.
 */
export const blockResourceMeta = (
  block: Pick<TContentBlockBase, 'blockType' | 'resourceType'> & {
    resourceIcon?: TContentBlockBase['resourceIcon'];
  },
): ContentBlockIconMeta => {
  const preset = presetResourceMeta(block);
  const override = block.resourceIcon ? CONTENT_BLOCK_ICONS[block.resourceIcon] : undefined;
  return override ? { ...preset, icon: override.icon } : preset;
};
